import React from "react";
import { SERVICES, FULL_PACKAGE, ADDONS } from "../data/site";
import { Settings, BrainCircuit, MonitorPlay, ArrowUpRight } from "lucide-react";

const ICONS = { Settings, BrainCircuit, MonitorPlay };

// ─────────────────────────────────────────────────────────────────────────────
// A service card matches the Photoshop reference exactly.
// Typography:
//   - Title (Design Execution / Ideation + Design / Full Video Packaging)
//                                                     → Clash Display Semibold (600)
//   - Subtitle (Perfect for creators who…)             → SF Pro Display Regular,
//                                                        solid white with opacity 0.7
//   - Bullet items                                      → SF Pro Display Regular
// ─────────────────────────────────────────────────────────────────────────────
function ServiceCard({ s, compact = false }) {
  const Ic = ICONS[s.icon];
  return (
    <div className="relative glass-card p-4 md:p-8 h-full flex flex-col">
      {/* Header row — title + icon */}
      <div className="flex items-start justify-between gap-3 md:gap-6">
        <div className="flex items-start gap-2 md:gap-3 min-w-0">
          <span
            className="rounded-full mt-1 shrink-0"
            style={{
              width: 3,
              height: compact ? 34 : 42,
              background: "#8B5CF6",
            }}
          />
          <div
            style={{
              fontFamily: "'Clash Display',sans-serif",
              fontWeight: 500,
              fontSize: compact
                ? "clamp(16px, 1.7vw, 26px)"
                : "clamp(20px, 2vw, 32px)",
              lineHeight: 1.05,
              letterSpacing: "-0.01em",
              color: "#FFFFFF",
            }}
          >
            {s.title[0]}
            <br />
            {s.title[1]}
          </div>
        </div>
        <div className="shrink-0 w-9 h-9 md:w-12 md:h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
          <Ic
            className="w-4 h-4 md:w-6 md:h-6 text-white"
            strokeWidth={1.6}
          />
        </div>
      </div>

      {/* Subtitle — solid white at 70% opacity, SF Pro Display Regular */}
      <p
        className="mt-3 md:mt-5"
        style={{
          fontFamily: "'SF Pro Display',sans-serif",
          fontWeight: 400,
          fontSize: compact ? "clamp(10px, 0.85vw, 14px)" : "clamp(12px, 0.95vw, 15px)",
          lineHeight: 1.5,
          color: "rgba(255,255,255,0.7)",
        }}
      >
        {s.subtitle}
      </p>

      {/* Shine divider */}
      <div
        aria-hidden="true"
        className="my-4 md:my-6 h-px w-full"
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.28) 50%, transparent 100%)",
        }}
      />

      {/* Bullets — SF Pro Display Regular. NOT pushed to bottom — start
          immediately after the divider so first-bullet positions align across
          adjacent cards (regardless of how many bullets each card has). */}
      <ul className="space-y-2 md:space-y-3">
        {s.bullets.map((b, i) => (
          <li
            key={i}
            className="flex items-start gap-2 md:gap-3"
            style={{
              fontFamily: "'SF Pro Display',sans-serif",
              fontWeight: 400,
              fontSize: compact ? "clamp(10px, 0.85vw, 14px)" : "clamp(12px, 0.95vw, 15px)",
              lineHeight: 1.4,
              color: "rgba(255,255,255,0.9)",
            }}
          >
            <ArrowUpRight
              className="w-3 h-3 md:w-4 md:h-4 text-[#8B5CF6] mt-0.5 md:mt-1 shrink-0"
              strokeWidth={2.25}
            />
            <span>{typeof b === "string" ? b : renderRich(b)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function renderRich(b) {
  if (!b.strong) return b.text;
  const parts = [];
  let rest = b.text;
  b.strong.forEach((s) => {
    const idx = rest.indexOf(s);
    if (idx >= 0) {
      parts.push(rest.slice(0, idx));
      parts.push(
        <strong
          key={s}
          style={{
            fontFamily: "'SF Pro Display',sans-serif",
            fontWeight: 500,
            color: "#FFFFFF",
          }}
        >
          {s}
        </strong>
      );
      rest = rest.slice(idx + s.length);
    }
  });
  parts.push(rest);
  return parts;
}

// ─────────────────────────────────────────────────────────────────────────────
// Add-on card (A/B Test Variations / Alternative Concepts / Additional Revisions)
// Title  → Clash Display Medium (500)
// Body   → SF Pro Display Regular
// ─────────────────────────────────────────────────────────────────────────────
function AddonCard({ title, body }) {
  return (
    <div className="reveal glass-card px-4 md:px-5 py-4 md:py-5 text-center">
      <span
        className="inline-block rounded-full"
        style={{
          padding: "6px 16px",
          background: "rgba(79,70,229,0.25)",
          border: "1px solid rgba(139,92,246,0.4)",
          color: "#FFFFFF",
          fontFamily: "'Clash Display',sans-serif",
          fontWeight: 500,
          fontSize: "clamp(12px, 1vw, 16px)",
          letterSpacing: "-0.005em",
        }}
      >
        {title}
      </span>
      <p
        className="mt-3"
        style={{
          fontFamily: "'SF Pro Display',sans-serif",
          fontWeight: 400,
          fontSize: "clamp(11px, 0.9vw, 14px)",
          lineHeight: 1.5,
          color: "rgba(255,255,255,0.75)",
        }}
      >
        {body}
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function Services() {
  return (
    <section id="services" className="relative py-6 md:py-14">
      <div className="max-w-5xl mx-auto px-5 md:px-8 text-center">
        {/* Heading — gradient Clash Display */}
        <h2
          className="reveal text-transparent bg-clip-text"
          style={{
            fontFamily: "'Clash Display',sans-serif",
            fontWeight: 500,
            fontSize: "clamp(54px, 8vw, 96px)",
            lineHeight: 0.95,
            letterSpacing: "-0.02em",
            backgroundImage:
              "linear-gradient(180deg,#EDE9FE 0%,#C4B5FD 50%,#8B5CF6 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            margin: 0,
          }}
        >
          Services
        </h2>

        {/* First sub-heading → Karvest Regular */}
        <div
          className="reveal mt-2"
          style={{
            fontFamily: "'Karvest',sans-serif",
            fontWeight: 400,
            fontSize: "clamp(13px, 1vw, 17px)",
            lineHeight: 1.45,
            color: "rgba(255,255,255,0.72)",
            letterSpacing: "0.005em",
          }}
        >
          Choose the Level of
          <br />
          Creative Support You Need.
        </div>

        <div className="reveal mx-auto w-40 md:w-56 h-[1px] mt-4 bg-white/20" />

        {/* Second sub-heading → Karvest Regular */}
        <p
          className="reveal mt-6 max-w-2xl mx-auto"
          style={{
            fontFamily: "'Karvest',sans-serif",
            fontWeight: 400,
            fontSize: "clamp(13px, 1vw, 17px)",
            lineHeight: 1.55,
            color: "rgba(255,255,255,0.82)",
            letterSpacing: "0.005em",
          }}
        >
          Whether you already have a thumbnail concept or need complete creative
          direction from strategy to final delivery, Clickory offers flexible
          services built around your workflow.
        </p>

        {/* Top row — 2 cards side-by-side on ALL viewports (per user + PSD ref) */}
        <div className="grid grid-cols-2 gap-3 md:gap-6 mt-10 md:mt-12 text-left items-stretch">
          {SERVICES.map((s) => (
            <div key={s.title.join(" ")} className="reveal">
              <ServiceCard s={s} compact />
            </div>
          ))}
        </div>

        {/* Full Video Packaging — slightly wider than top cards on mobile but
            NOT full-width; centered in the frame */}
        <div className="reveal mt-6 md:mt-8 text-left mx-auto w-[80%] max-w-[420px] md:w-full md:max-w-[520px]">
          <ServiceCard s={FULL_PACKAGE} />
        </div>

        {/* Addon row — A/B Test + Alternative Concepts side-by-side */}
        <div className="grid grid-cols-2 gap-3 md:gap-6 mt-8 md:mt-10 max-w-3xl mx-auto">
          {ADDONS.slice(0, 2).map((a) => (
            <AddonCard key={a.title} title={a.title} body={a.body} />
          ))}
        </div>

        {/* Additional Revisions — centered below */}
        <div className="mt-3 md:mt-6 flex justify-center">
          <div className="w-full max-w-md">
            <AddonCard title={ADDONS[2].title} body={ADDONS[2].body} />
          </div>
        </div>
      </div>
    </section>
  );
}
